import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { financeApi } from '@/services/finance'
import { JobTitleLink } from '@/components/jobs/job-title-link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getPaymentMethodLabel = (method: unknown) => {
  if (method && typeof method === 'object') {
    const wallet = method as {
      id?: string | number
      label?: string | null
      wallet_name?: string | null
      wallet_variant_name?: string | null
    }
    return wallet.label || wallet.wallet_name || wallet.wallet_variant_name || `Wallet ${wallet.id ?? ''}`.trim()
  }

  if (typeof method === 'string' || typeof method === 'number') {
    return String(method)
  }

  return '-'
}

export default function InvoicePaymentsListPage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const paymentsQuery = useQuery({
    queryKey: ['finance-invoice-payments-list', serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoicePayments({ page_size: 200, service_instance_id: serviceInstanceId ?? undefined }),
  })

  const payments = paymentsQuery.data?.results ?? []

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoice Payments</CardTitle>
        </CardHeader>
        <CardContent>
          {paymentsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : payments.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              No invoice payments found.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border/70">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice No</TableHead>
                    <TableHead>Job Id</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={String(payment.id)}>
                      <TableCell>{payment.invoice_number || `INV-${payment.invoice}`}</TableCell>
                      <TableCell>{String(payment.job_id ?? '-')}</TableCell>
                      <TableCell>
                        <JobTitleLink
                          jobId={payment.job_id}
                          title={payment.job_title}
                          serviceInstanceId={serviceInstanceId}
                        />
                      </TableCell>
                      <TableCell>{payment.payment_date ? formatDate(payment.payment_date) : '-'}</TableCell>
                      <TableCell>{getPaymentMethodLabel(payment.payment_method)}</TableCell>
                      <TableCell>{payment.reference || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(toNumber(payment.amount))}</TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="outline" size="sm">
                          <Link to={withServiceInstanceId(`/finance/invoices/${payment.invoice}/payments`, serviceInstanceId)}>
                            Open
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
